import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import type { FloatingPanelPosition } from '@zag/ui';
import type { SubmitTestProtocolResponse, TestCaseResultStatus, TestScriptResponse } from '@zag/control/models';

/** DNS label that marks the current host as an issue verification instance. */
const ISSUES_ENV_LABEL = '.issues.';
const ISSUE_SUBDOMAIN_PATTERN = /^issue-0*(\d+)$/;
const STORAGE_KEY_PREFIX = 'sc-test-feedback';

/** A recorded outcome for a single test case. */
export interface RecordedResult {
  status: Extract<TestCaseResultStatus, 'passed' | 'failed'>;
  comment: string;
}

export type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; status: number | null }
  | { kind: 'done'; commentUrl: string };

interface StoredReviewState {
  imageTag: string | null;
  results: Record<string, RecordedResult>;
  tester: string | null;
  panel: { position: FloatingPanelPosition | null; collapsed: boolean };
  lastCommentUrl: string | null;
}

const EMPTY_STORED_STATE: StoredReviewState = {
  imageTag: null,
  results: {},
  tester: null,
  panel: { position: null, collapsed: false },
  lastCommentUrl: null,
};

/**
 * State and API access for the issue verification test sidebar.
 *
 * Only ever instantiated on issue verification instances — the sidebar
 * component is the sole consumer and is itself gated by
 * {@link isIssueVerificationHost} in the layout, so no verification request
 * is issued on other environments (AC 2).
 *
 * Review progress is client-side only: persisted in localStorage keyed by
 * issue number, scoped to the deployed image tag (a new build resets the
 * review, AC 9). Finished protocols live on GitHub.
 */
@Injectable({ providedIn: 'root' })
export class TestFeedbackService {
  private readonly http = inject(HttpClient);

  readonly issueNumber = resolveIssueNumber(typeof window === 'undefined' ? '' : window.location.hostname);

  /** Parsed Test Script for this instance; `params` is undefined off issue hosts, keeping the resource idle. */
  readonly scriptResource = rxResource({
    params: () => this.issueNumber ?? undefined,
    stream: () => this.http.get<TestScriptResponse>('/api/verification/test-script'),
  });

  private readonly stored = signal<StoredReviewState>(this.readStorage());

  /**
   * Stored results are stale when they were recorded against a different
   * build than the one currently deployed (AC 9). `imageTag === null` in
   * storage means "never recorded anything" and is never stale.
   */
  private readonly storedResultsStale = computed(() => {
    const script = this.scriptResource.value();
    return !!script && this.stored().imageTag !== null && this.stored().imageTag !== script.imageTag;
  });

  /**
   * Recorded results, revalidated against the loaded script's image tag:
   * results recorded for an older build are ignored (AC 9). Recording a new
   * result stamps the current image tag and physically drops the stale set.
   */
  readonly results = computed<Record<string, RecordedResult>>(() => (this.storedResultsStale() ? {} : this.stored().results));

  /** True while a stale review is being ignored — cleared by the first new recording. */
  readonly buildChanged = computed(() => this.storedResultsStale() && Object.keys(this.stored().results).length > 0);

  readonly tester = computed(() => this.stored().tester);
  readonly panelPosition = computed(() => this.stored().panel.position);
  readonly panelCollapsed = computed(() => this.stored().panel.collapsed);
  readonly lastCommentUrl = computed(() => this.stored().lastCommentUrl);

  private readonly submitStateInternal = signal<SubmitState>({ kind: 'idle' });
  readonly submitState = this.submitStateInternal.asReadonly();

  readonly resolvedCount = computed(() => Object.keys(this.results()).length);
  readonly totalCount = computed(() => this.scriptResource.value()?.script.testCases.length ?? 0);
  readonly unresolvedCount = computed(() => Math.max(0, this.totalCount() - this.resolvedCount()));

  recordResult(caseId: string, result: RecordedResult): void {
    const script = this.scriptResource.value();
    // Stamp the current build so stale-review detection (AC 9) keys on it.
    const imageTag = script ? script.imageTag : this.stored().imageTag;
    this.patchStorage({ imageTag, results: { ...this.results(), [caseId]: result } });
  }

  clearResult(caseId: string): void {
    const { [caseId]: _removed, ...rest } = this.results();
    this.patchStorage({ results: rest });
  }

  saveTester(tester: string): void {
    this.patchStorage({ tester });
  }

  savePanelState(panel: { position: FloatingPanelPosition | null; collapsed: boolean }): void {
    this.patchStorage({ panel });
  }

  /** Reset for a fresh review round after a protocol was posted (AC 12). */
  startNewReview(): void {
    const script = this.scriptResource.value();
    this.patchStorage({ imageTag: script ? script.imageTag : this.stored().imageTag, results: {}, lastCommentUrl: null });
    this.submitStateInternal.set({ kind: 'idle' });
  }

  /**
   * Post the finished review. Unrecorded cases are submitted as `not-tested`
   * (AC 11); recorded results stay in storage until the post succeeds (AC 13).
   */
  submitProtocol(tester: string): void {
    const script = this.scriptResource.value();
    if (!script || this.submitStateInternal().kind === 'submitting') return;

    const results = script.script.testCases.map(testCase => {
      const recorded = this.results()[testCase.id];
      if (!recorded) return { id: testCase.id, status: 'not-tested' as const };
      return recorded.status === 'failed'
        ? { id: testCase.id, status: recorded.status, comment: recorded.comment }
        : { id: testCase.id, status: recorded.status };
    });

    this.submitStateInternal.set({ kind: 'submitting' });
    this.saveTester(tester);

    this.http
      .post<SubmitTestProtocolResponse>('/api/verification/protocol', { tester, imageTag: script.imageTag ?? undefined, results })
      .subscribe({
        next: response => {
          this.patchStorage({ lastCommentUrl: response.commentUrl });
          this.submitStateInternal.set({ kind: 'done', commentUrl: response.commentUrl });
        },
        error: (error: { status?: number }) => {
          this.submitStateInternal.set({ kind: 'error', status: error.status ?? null });
        },
      });
  }

  // ─── Storage ────────────────────────────────────────────────────────────────

  private get storageKey(): string {
    return `${STORAGE_KEY_PREFIX}:${this.issueNumber ?? 'none'}`;
  }

  private readStorage(): StoredReviewState {
    if (typeof localStorage === 'undefined') return EMPTY_STORED_STATE;
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? { ...EMPTY_STORED_STATE, ...(JSON.parse(raw) as Partial<StoredReviewState>) } : EMPTY_STORED_STATE;
    } catch {
      // Corrupt storage entries must never break the sidebar — start fresh.
      return EMPTY_STORED_STATE;
    }
  }

  private patchStorage(patch: Partial<StoredReviewState>): void {
    const next = { ...this.stored(), ...patch };
    this.stored.set(next);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(next));
      } catch {
        // Quota/private-mode failures degrade to in-memory persistence only.
      }
    }
  }
}

/**
 * An issue verification host is `issue-<n>` on the issues environment
 * (e.g. `issue-253.issues.smartcapacity.dev`). Everything else — production,
 * demo, local dev — yields `null` and the sidebar never activates.
 */
export function resolveIssueNumber(hostname: string): number | null {
  if (!hostname.includes(ISSUES_ENV_LABEL)) return null;
  const match = ISSUE_SUBDOMAIN_PATTERN.exec(hostname.split('.')[0] ?? '');
  return match ? Number(match[1]) : null;
}

/** Cheap gate for the layout — no service construction, no network. */
export function isIssueVerificationHost(): boolean {
  return typeof window !== 'undefined' && resolveIssueNumber(window.location.hostname) !== null;
}
